import { useStore } from 'stimulus-store';
import TomSelect from "tom-select";
import { getRegionsForSelect } from "shared/locales/locales";
import { updateSelect } from "shared/selects/select_helpers";
import _, { isEmpty } from "lodash";
import { Controller } from "@hotwired/stimulus";
import { regionStore } from "../../../stores/locales/region_store";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { LOCALE_TYPE_REGION, LOCALE_TYPE_BUILDING, LOCALE_TYPE_AREA } from "constants/facilities_constants";

// Connects to data-controller="selects--locales--region-select"
//
// TURBO DRIVE NOTE: regionStore is a module-level singleton that persists across
// Turbo navigations. This controller writes to regionStore when the user makes a
// selection, and clears it again in disconnect() when this controller's element
// is torn down (e.g. on Turbo Drive navigation to another page). Clearing on
// disconnect rather than relying on a separate reset-on-connect controller on
// the NEXT page avoids a race: the old page's clear and the new page's hydration
// used to be two independently-scheduled operations with no ordering guarantee
// between them, so a slow-to-resolve hydration fetch could occasionally be
// clobbered by a reset that ran later. Tying the clear to this controller's own
// disconnect makes it a precondition of the next page's controller existing at
// all, rather than a competitor racing against it.
export default class extends Controller {

    static stores = [
        regionStore
    ]

    static values = {
        selected: String,
        url: String,
        syncUrl: Boolean,

        // Set only by pages that need organization-wide (not active-project) results,
        // e.g. the Reports filter builder. The token is minted server-side (signed,
        // short-lived, bound to the current user) when that page is rendered — this
        // controller just relays it verbatim. Left unset, this controller's behavior
        // is unchanged on every other page.
        projectScopeToken: String
    }

    connect() {
        useStore(this);
    }

    disconnect() {
        this.resetRegionStore();
    }

    initialize() {
        this._createRegionSelect();
    }

    _createRegionSelect() {
        const regionSelectElement = this.element;
        const selectedRegion = this.selectedValue;

        getRegionsForSelect(this.projectScopeTokenValue).then((results) => {
            const control = new TomSelect(regionSelectElement, {
                placeholder: 'Select a region',
                create: false,
                valueField: 'id',
                labelField: 'text',
                searchField: 'text',
                sortField: {
                    field: "text",
                    direction: "asc"
                },
                onChange: (event) => this._regionSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (values) => this._regionUnselectedEvent(values), // Keep this separate or page loads will be broken
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: true // This closes the select after a selection is made.
            });

            // Add the expected options to the select. We're doing it this way instead of
            // in the constructor so the items appear in the underlying select. This makes
            // it possible to test using capybara matchers
            updateSelect(this.element.id, results);

            // Syncing will ensure options are stubbed out within the select element. This
            // ensures the tests will have the data in the DOM it can test against
            control.sync();

            const params = new URLSearchParams(window.location.search);
            const regionFromUrl = params.get(LOCALE_TYPE_REGION);

            if (!_.isEmpty(selectedRegion)) {
                // Existing: server-rendered pre-selection
                // Setting silent to true suppresses change events
                // https://tom-select.js.org/docs/api/
                control.setValue(selectedRegion, DO_NOT_EMIT_EVENTS);

                // Inform others what region is selected
                const selectedOption = control.options[selectedRegion];
                this.setRegionStoreValue({
                    id: selectedRegion,
                    name: selectedOption.text
                });
            } else if (regionFromUrl) {
                // URL-parameter pre-selection
                const selectedOption = control.options[regionFromUrl];
                if (selectedOption) {
                    control.setValue(regionFromUrl, DO_NOT_EMIT_EVENTS);
                    this.setRegionStoreValue({ id: regionFromUrl, name: selectedOption.text });
                } else {
                    // Invalid region ID — clean up URL
                    this._removeRegionIdFromUrl();
                }
            }
        });
    }

    _regionSelectionChanged(regionId) {
        // We only want to process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(regionId)) {
            return;
        }

        // Get the selected option text for the region name
        const selectedOption = this.element.tomselect.options[regionId];
        const regionName = selectedOption.text;

        // Update the store with the region's id and name
        this.setRegionStoreValue({
            id: regionId,
            name: regionName
        });

        /*
         * Send an event telling the building, area, and room select
         * controllers to enable those elements. I stole this method from here:
         * https://lmika.org/2021/02/17/communication-among-stimulus.html
         */
        window.dispatchEvent(new CustomEvent('regionSelectionMade', {
            detail: {
                region: regionId
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._addRegionIdToUrl(regionId);
        }
    }

    _regionUnselectedEvent(values) {
        // Clear the region store
        this.setRegionStoreValue({});

        window.dispatchEvent(new Event('regionSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._removeRegionIdFromUrl();
        }
    }

    _addRegionIdToUrl(regionId) {
        const params = new URLSearchParams(window.location.search);
        params.set(LOCALE_TYPE_REGION, regionId);
        this._replaceUrl(params);
    }

    _removeRegionIdFromUrl() {
        const params = new URLSearchParams(window.location.search);
        params.delete(LOCALE_TYPE_REGION);
        params.delete(LOCALE_TYPE_BUILDING);
        params.delete(LOCALE_TYPE_AREA);
        this._replaceUrl(params);
    }

    _replaceUrl(params) {
        const queryString = params.toString();
        const newUrl = queryString
            ? `${window.location.pathname}?${queryString}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
        history.replaceState(null, '', newUrl);
    }
}
